// @greenlight-spec: 41-groups-management.md
// @greenlight-spec: 28-it-dashboard-ui.md
import type {
  GroupListItem,
  GroupMember,
  IdpGroupRegistration,
  UserListItem,
} from '@greenlight/shared';

/** The two kinds of row the Groups section lists, told apart by where membership lives. */
export type GroupKind = 'greenlight' | 'idp';

export interface GroupRow {
  kind: GroupKind;
  id: string;
  /** A Greenlight group's name, or IT's label for a registered IdP group. */
  name: string;
  description: string | null;
  /** Members for a Greenlight group; signed-in users Greenlight has synced for an IdP one. */
  memberCount: number;
  appCount: number;
  /** Present only on IdP rows — the identifier IT pasted at registration. */
  identifier?: string;
}

export function toGroupRow(group: GroupListItem): GroupRow {
  return {
    kind: 'greenlight',
    id: group.id,
    name: group.name,
    description: group.description,
    memberCount: group.member_count,
    appCount: group.app_count,
  };
}

export function toIdpGroupRow(registration: IdpGroupRegistration): GroupRow {
  return {
    kind: 'idp',
    id: registration.id,
    name: registration.label,
    description: registration.description,
    memberCount: registration.known_member_count,
    appCount: registration.app_count,
    identifier: registration.idp_group_id,
  };
}

/**
 * One list, both sources, ordered by name so a reader scans it as a single
 * inventory rather than two stacked tables (docs/28 §/users).
 */
export function mergeGroupRows(
  groups: GroupListItem[],
  registrations: IdpGroupRegistration[],
): GroupRow[] {
  return [...groups.map(toGroupRow), ...registrations.map(toIdpGroupRow)].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

export const SOURCE_LABEL: Record<GroupKind, string> = {
  greenlight: 'Greenlight group',
  idp: 'Directory group',
};

/**
 * Why removal is refused, or null when it is allowed. A bound group must never
 * be deletable: doing so would silently return an app to default-open
 * (docs/41 §The group model).
 */
export function removalBlockedReason(row: GroupRow): string | null {
  if (row.appCount === 0) return null;
  const apps = `${row.appCount} ${row.appCount === 1 ? 'app' : 'apps'}`;
  return row.kind === 'greenlight'
    ? `Bound to ${apps}. Remove it from their Access tabs before deleting.`
    : `Bound to ${apps}. Remove it from their Access tabs before deregistering.`;
}

/**
 * Takes the count explicitly rather than reading `row.memberCount`, so a
 * surface rendering an actual member list can label what it is showing instead
 * of the server's org-wide figure.
 */
export function memberCountLabel(kind: GroupKind, count: number): string {
  const noun = count === 1 ? 'member' : 'members';
  return kind === 'idp' ? `${count} signed-in ${noun}` : `${count} ${noun}`;
}

/**
 * What a drawer rendering an actual member list may claim. Both kinds can hold
 * fewer members than the server counts — a directory group's list is bounded by
 * the page of the roster loaded on /users, a Greenlight group's by the single
 * page of members the drawer fetches — so a short list names both numbers
 * rather than reporting either as the whole truth.
 */
export function shownMemberCountLabel(kind: GroupKind, shown: number, known: number): string {
  if (shown >= known) return memberCountLabel(kind, shown);
  const noun = known === 1 ? 'member' : 'members';
  return `showing ${shown} of ${known} ${kind === 'idp' ? 'signed-in ' : ''}${noun}`;
}

export function appCountLabel(count: number): string {
  return count === 1 ? '1 app' : `${count} apps`;
}

/**
 * Users the add-member picker may offer: everyone on the loaded page who is not
 * already in the group, narrowed by a case-insensitive name/email match.
 */
export function candidateMembers(
  users: UserListItem[],
  members: GroupMember[],
  query: string,
): UserListItem[] {
  const taken = new Set(members.map((m) => m.id));
  const q = query.trim().toLowerCase();
  return users.filter((u) => {
    if (taken.has(u.id)) return false;
    if (!q) return true;
    return u.email.toLowerCase().includes(q) || (u.display_name ?? '').toLowerCase().includes(q);
  });
}

/** Splits an inspector's membership list into the editable and the read-only halves. */
export function splitMemberships<T extends { source: 'idp' | 'greenlight' }>(
  memberships: T[],
): { greenlight: T[]; idp: T[] } {
  return {
    greenlight: memberships.filter((g) => g.source === 'greenlight'),
    idp: memberships.filter((g) => g.source === 'idp'),
  };
}
